import React, { useEffect, useMemo, useState } from 'react';
import { PiggyBank, Pencil, Loader2, Check, XCircle } from 'lucide-react';
import { useHouseholdStore } from '../../store/useStore.js';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export default function DownPaymentTracker() {
  const {
    household,
    updateSavings,
    loadHousehold,
    isLoading,
  } = useHouseholdStore();

  const [isEditing, setIsEditing] = useState(false);
  const [draftSavings, setDraftSavings] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (household?.current_savings !== undefined) {
      setDraftSavings(household.current_savings);
    }
  }, [household?.current_savings]);

  const totals = useMemo(() => {
    const current = household?.current_savings ?? 0;
    const target = household?.target_down_payment ?? 0;
    const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0;
    const remaining = target > 0 ? Math.max(target - current, 0) : 0;

    return {
      current,
      target,
      progress: Math.round(progress),
      remaining,
    };
  }, [household?.current_savings, household?.target_down_payment]);

  const handleSave = async () => {
    setError('');

    if (Number.isNaN(Number(draftSavings))) {
      setError('Enter a valid number for current savings.');
      return;
    }

    setSubmitting(true);
    try {
      await updateSavings(Number(draftSavings));
      await loadHousehold();
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Failed to update savings.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDraftSavings(household?.current_savings ?? 0);
    setIsEditing(false);
    setError('');
  };

  const isLoaded = household && !isLoading;

  return (
    <section className="bg-white rounded-xl shadow-lg p-5 border border-indigo-100">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 text-indigo-600 rounded-full">
            <PiggyBank className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm text-indigo-600 font-semibold uppercase tracking-wide">
              Down Payment Progress
            </p>
            <h2 className="text-xl font-bold text-gray-800">Build Your Nest Egg</h2>
          </div>
        </div>
        {!isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-semibold"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
        )}
      </header>

      {!isLoaded ? (
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading savings details…</span>
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-sm text-gray-600">
              Current: <span className="font-semibold text-gray-900">{currency.format(totals.current)}</span>
              {' '}/ Target: <span className="font-semibold text-gray-900">{currency.format(totals.target || 0)}</span>
            </p>
            <div className="mt-3 h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all"
                style={{ width: `${totals.progress}%` }}
              />
            </div>
            {totals.target > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                {totals.remaining > 0
                  ? `${currency.format(totals.remaining)} left to reach your goal`
                  : 'Goal reached! Time to find the perfect home.'}
              </p>
            )}
          </div>

          {isEditing && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Current Savings Amount
              </label>
              <input
                type="number"
                value={draftSavings}
                onChange={(event) => setDraftSavings(Number(event.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />

              {error && (
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  {error}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={submitting}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
